import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Ticket, AlertCircle } from "lucide-react";

const ERROR_MESSAGES: Record<string, string> = {
  auth_required: "Please sign in with the exact email your invite was sent to.",
  not_found: "We couldn't find that invite code. Double-check and try again.",
  already_redeemed: "This invite has already been used.",
  revoked: "This invite was revoked. Please contact hello@wekit.ai.",
  expired: "This invite has expired. Please request a new one.",
  email_mismatch: "This invite is locked to a different email. Sign in with the email the invite was sent to.",
  already_has_access: "You already have full access — head straight to your assessment.",
  invalid_code: "That code doesn't look right. It should look like WEKIT-XXXX-XXXX.",
  server_error: "Something went wrong. Please try again in a moment.",
};

export default function Redeem() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [code, setCode] = useState((params.get("code") || "").toUpperCase());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const q = (params.get("code") || "").toUpperCase();
    if (q) setCode(q);
  }, [params]);

  const onRedeem = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    if (!user) {
      const next = `/redeem${code ? `?code=${encodeURIComponent(code)}` : ""}`;
      nav(`/auth?next=${encodeURIComponent(next)}`);
      return;
    }
    const trimmed = code.trim().toUpperCase();
    if (!/^WEKIT-[A-Z0-9]{4}-[A-Z0-9]{4}$/.test(trimmed) && !/^[A-Z0-9-]{6,40}$/.test(trimmed)) {
      setError(ERROR_MESSAGES.invalid_code);
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: err } = await supabase.functions.invoke("redeem-comp-invite", {
        body: { code: trimmed },
      });
      if (err || !data?.ok) {
        const key = (data?.error as string) || "server_error";
        setError(ERROR_MESSAGES[key] || ERROR_MESSAGES.server_error);
        return;
      }
      setSuccess(true);
      toast.success("Invitation accepted. Starting your assessment…");
      setTimeout(() => nav("/start"), 1200);
    } catch (e) {
      console.error(e);
      setError(ERROR_MESSAGES.server_error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <PageHead
        title="Redeem your invite | WeKIT™ Career Clarity 360"
        description="Redeem your complimentary WeKIT™ Career Clarity 360 assessment invite."
        path="/redeem"
        noIndex
      />
      <Card className="max-w-md w-full rounded-3xl border-2">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-2 text-primary">
            <Ticket className="h-5 w-5" />
            <span className="text-xs uppercase tracking-wider font-semibold">Complimentary access</span>
          </div>
          <CardTitle className="text-2xl">Redeem your invite</CardTitle>
          <p className="text-sm text-muted-foreground">
            Enter the code from your email to unlock the WeKIT™ Career Clarity 360 assessment.
          </p>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="flex flex-col items-center text-center py-6 gap-3">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <p className="font-medium">You're in. Loading your assessment…</p>
            </div>
          ) : (
            <form onSubmit={onRedeem} className="space-y-4">
              <div>
                <Label htmlFor="code">Invite code</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="WEKIT-XXXX-XXXX"
                  autoComplete="off"
                  spellCheck={false}
                  className="font-mono tracking-widest text-center h-12 text-base mt-2"
                  disabled={submitting}
                />
              </div>

              {!user && !authLoading && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    You'll be asked to sign in next. Use the exact email the invite was sent to.
                  </AlertDescription>
                </Alert>
              )}

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full h-12" disabled={submitting || !code}>
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redeeming…</>
                ) : user ? (
                  "Redeem & start assessment"
                ) : (
                  "Sign in to redeem"
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Trouble? Email <a href="mailto:hello@wekit.ai" className="underline">hello@wekit.ai</a>.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
